import { useMemo } from "react";
import PropTypes from "prop-types";
import { Card, Chip, Grid, Icon, IconButton, InputLabel, Switch, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import { useMaterialUIController } from "context";
import {
    AGRUPAR_DATAS, FUNCOES_VALOR, LAYOUTS, LIMITES, MAX_ABAS, MOSTRAR_COMO, SUBTOTAIS, calcularTabelaDinamica, configVazia, ehPercentual, formatarValor,
    nomeDoValor, nomesDasAbas, novaTabela, temConteudo, tipoDoCampo,
} from "./tabelaDinamica";
import { ESCURO } from "assets/coresTema";

/**
 * Monta as tabelas dinâmicas com os dados já carregados na tela: cada aba do painel vira uma aba da planilha, todas
 * sobre a mesma base. Em cada uma: linhas, colunas, valores (com percentual) e filtros, com prévia ao vivo. Ligada em
 * "Incluir na planilha", a planilha baixada leva as abas com conteúdo, como tabelas dinâmicas nativas do Excel.
 */
export default function TabelaDinamicaPainel({ dados, tabelas, ativa, onTabelas, onAtiva, incluir, onIncluir, sugestao, rotulo }) {
    const indiceAtivo = Math.min(Math.max(ativa, 0), tabelas.length - 1);
    const config = tabelas[indiceAtivo];
    const nomes = nomesDasAbas(tabelas);
    const comConteudo = tabelas.filter(temConteudo).length;
    const salvar = (parte) => onTabelas(tabelas.map((t, i) => (i === indiceAtivo ? { ...t, ...parte } : t)));
    const novaAba = () => {
        onTabelas([...tabelas, novaTabela()]);
        onAtiva(tabelas.length);
    };
    const duplicarAba = () => {
        const copia = novaTabela({ ...config, nome: config.nome ? `${config.nome} (cópia)` : "" });
        onTabelas([...tabelas.slice(0, indiceAtivo + 1), copia, ...tabelas.slice(indiceAtivo + 1)]);
        onAtiva(indiceAtivo + 1);
    };
    const excluirAba = () => {
        const resto = tabelas.filter((_, i) => i !== indiceAtivo);
        onTabelas(resto);
        onAtiva(Math.max(0, indiceAtivo - 1));
        if (!resto.some(temConteudo)) onIncluir(false);
    };
    const moverAba = (passo) => {
        const destino = indiceAtivo + passo;
        if (destino < 0 || destino >= tabelas.length) return;
        const lista = [...tabelas];
        [lista[indiceAtivo], lista[destino]] = [lista[destino], lista[indiceAtivo]];
        onTabelas(lista);
        onAtiva(destino);
    };
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const textoPrincipal = darkMode ? "#FFFFFF" : "#344767";
    const textoSecundario = darkMode ? "rgba(255, 255, 255, 0.8)" : "#6c757d";
    const labelStyle = { pb: 0.5 }; // aparência do rótulo vem do tema
    const borda = darkMode ? ESCURO.borda : "#e6e9ee";

    const campos = useMemo(() => (dados.length ? Object.keys(dados[0]) : []), [dados]);
    const tipos = useMemo(() => Object.fromEntries(campos.map(c => [c, tipoDoCampo(c, dados)])), [campos, dados]);
    const previa = useMemo(() => (temConteudo(config) ? calcularTabelaDinamica(dados, config) : null), [dados, config]);

    const usados = new Set([...config.linhas, ...config.colunas, ...config.filtros]);
    const opcao = (c) => ({ value: c, label: `${rotulo(c)}${tipos[c] === "data" ? " (data)" : ""}` });
    const opcoesArea = (area) => campos.filter(c => !usados.has(c) || config[area].includes(c)).map(opcao);
    const temData = [...config.linhas, ...config.colunas, ...config.filtros].some(c => tipos[c] === "data");
    const layout = config.layout === "compacto" ? "compacto" : "tabular";
    const subtotais = config.subtotais === "todos" ? "todos" : "primeiro";

    // Qualquer mudança feita pelo usuário já liga "Incluir na planilha"
    const mudar = (novo) => {
        salvar(novo);
        if (!incluir && temConteudo(novo)) onIncluir(true);
    };
    const mudarArea = (area, opts) => mudar({ ...config, [area]: (opts || []).slice(0, LIMITES[area]).map(o => o.value) });
    const mudarValor = (i, parte) => mudar({ ...config, valores: config.valores.map((v, j) => (j === i ? { ...v, ...parte } : v)) });
    const adicionarValor = () => {
        const numerico = campos.find(c => tipos[c] === "numero" && !config.valores.some(v => v.campo === c));
        const novo = numerico ? { campo: numerico, funcao: "sum" } : { campo: campos[0], funcao: "count" };
        mudar({ ...config, valores: [...config.valores, { ...novo, mostrarComo: "normal" }] });
    };
    // Atalho: o mesmo valor em % do total, logo abaixo
    const duplicarComoPercentual = (i) => {
        const v = config.valores[i];
        const lista = [...config.valores];
        lista.splice(i + 1, 0, { campo: v.campo, funcao: v.funcao, mostrarComo: "percentTotal" });
        mudar({ ...config, valores: lista.slice(0, LIMITES.valores) });
    };

    const chip = (ativo, label, onClick) => (
        <Chip
            key={label}
            label={label}
            clickable
            onClick={onClick}
            sx={{
                fontWeight: ativo ? 700 : 500,
                color: ativo ? "#FFFFFF" : textoPrincipal,
                backgroundColor: ativo ? "#1A73E8" : (darkMode ? "rgba(255,255,255,0.08)" : "#f0f2f5"),
                "&:hover": { backgroundColor: ativo ? "#1662C4" : (darkMode ? "rgba(255,255,255,0.16)" : "#e4e8ee") },
            }}
        />
    );

    const celulaCab = {
        padding: "6px 10px",
        fontWeight: 700,
        fontSize: "0.75rem",
        whiteSpace: "nowrap",
        color: darkMode ? "#FFFFFF" : "#344767",
        backgroundColor: darkMode ? ESCURO.painelEscuro : "#f1f3f6",
        borderBottom: `1px solid ${borda}`,
        textAlign: "left",
        verticalAlign: "top",
    };
    const celula = { padding: "5px 10px", fontSize: "0.8rem", whiteSpace: "nowrap", color: textoPrincipal, borderBottom: `1px solid ${borda}`, verticalAlign: "top" };
    const celulaNum = { ...celula, textAlign: "right", fontVariantNumeric: "tabular-nums" };
    const fundoTotal = darkMode ? "rgba(255,255,255,0.06)" : "#fafafa";
    const celulaTotal = { ...celulaNum, fontWeight: 700, backgroundColor: fundoTotal };
    const nValores = previa ? previa.valores.length : 0;
    // Colunas de rótulo: no tabular, uma por campo das linhas; no compacto, uma só com recuo. Sem campo, uma ("Todos")
    const camposLinha = config.linhas.length && layout === "tabular" ? config.linhas : [null];
    const chavesValor = previa ? (previa.temColunas ? [...previa.colunas.map(c => c.chave), "__total"] : ["__total"]) : [];
    const bordaGrupo = (i) => (i === 0 && previa?.temColunas ? `1px solid ${borda}` : undefined);

    const rotulosDaLinha = (l) => {
        const subtotal = l.tipo === "subtotal";
        const grupo = l.tipo === "grupo";
        const estilo = { ...celula, ...(subtotal ? { fontWeight: 700, backgroundColor: fundoTotal } : grupo ? { fontWeight: 700 } : {}) };
        if (layout === "compacto" || !config.linhas.length) {
            const texto = l.rotulos.length ? l.rotulos[l.rotulos.length - 1] : "Todos";
            return [<td key="r" style={{ ...estilo, paddingLeft: `${10 + 16 * (subtotal || grupo ? l.nivel : Math.max(config.linhas.length - 1, 0))}px` }}>{texto}</td>];
        }
        // Tabular: rótulos repetidos em toda linha do grupo; o subtotal leva "<grupo> Total" na coluna do campo
        return config.linhas.map((c, ri) => {
            let texto = "";
            if (!subtotal) texto = l.rotulos[ri];
            else if (ri < l.nivel) texto = l.rotulos[ri];
            else if (ri === l.nivel) texto = `${l.rotulos[ri]} Total`;
            return <td key={c} style={estilo}>{texto}</td>;
        });
    };

    return (
        <Card variant="outlined" sx={{ p: 2, mb: 3, background: darkMode ? ESCURO.cartao : "#fff", borderColor: darkMode ? ESCURO.borda : "#ddd" }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1.5}>
                <MDBox>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ color: textoPrincipal }}>
                        <Icon sx={{ verticalAlign: "middle", mr: 0.75 }}>pivot_table_chart</Icon>Tabelas dinâmicas
                    </MDTypography>
                    <MDTypography variant="caption" display="block" sx={{ color: textoSecundario }}>
                        Cada aba aqui vira uma aba da planilha, todas com a mesma base. Escolha o que vai nas linhas, nas colunas e o que somar ou contar (em valor ou em %).
                    </MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center" sx={{ pl: 1 }}>
                    <Switch checked={incluir && comConteudo > 0} disabled={comConteudo === 0} onChange={(e) => onIncluir(e.target.checked)} inputProps={{ "aria-label": "Incluir na planilha" }} />
                    <MDTypography variant="button" fontWeight="medium" sx={{ color: textoPrincipal }}>
                        Incluir na planilha{comConteudo > 1 ? ` (${comConteudo} abas)` : ""}
                    </MDTypography>
                </MDBox>
            </MDBox>

            {/* Abas: uma por tabela dinâmica, na ordem em que vão para a planilha */}
            <MDBox display="flex" alignItems="flex-end" gap={0.5} flexWrap="wrap" sx={{ borderBottom: `1px solid ${borda}`, mb: 1.5 }} role="tablist">
                {tabelas.map((t, i) => {
                    const ativo = i === indiceAtivo;
                    return (
                        <MDBox
                            key={t.id}
                            component="button"
                            type="button"
                            role="tab"
                            aria-selected={ativo}
                            onClick={() => onAtiva(i)}
                            sx={{
                                border: "none",
                                cursor: "pointer",
                                px: 1.5,
                                py: 0.75,
                                fontSize: "0.78rem",
                                fontWeight: ativo ? 700 : 500,
                                textTransform: "uppercase",
                                letterSpacing: "0.02em",
                                color: ativo ? (darkMode ? "#FFFFFF" : "#1A1A1A") : textoSecundario,
                                background: ativo ? (darkMode ? ESCURO.painel : "#fafafa") : "transparent",
                                borderRadius: "8px 8px 0 0",
                                borderBottom: ativo ? "3px solid #2E7D32" : "3px solid transparent",
                                opacity: temConteudo(t) || ativo ? 1 : 0.7,
                                "&:hover": { background: darkMode ? "rgba(255,255,255,0.06)" : "#f3f5f8" },
                            }}
                        >
                            {nomes[i]}
                        </MDBox>
                    );
                })}
                {tabelas.length < MAX_ABAS && (
                    <Tooltip title="Nova aba com outra tabela dinâmica (mesma base)">
                        <IconButton size="small" onClick={novaAba} aria-label="Nova aba" sx={{ mb: 0.5 }}>
                            <Icon sx={{ color: darkMode ? "#90CAF9" : "#1A73E8" }}>add</Icon>
                        </IconButton>
                    </Tooltip>
                )}
            </MDBox>

            {/* Aba ativa: nome e ações */}
            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
                <MDBox sx={{ flex: "0 1 320px", minWidth: 200 }}>
                    <MDInput
                        fullWidth
                        size="small"
                        label="Nome da aba"
                        value={config.nome || ""}
                        placeholder={nomes[indiceAtivo]}
                        onChange={(e) => salvar({ nome: e.target.value.slice(0, 31) })}
                        InputLabelProps={{ shrink: true }}
                    />
                </MDBox>
                <Tooltip title="Mover para a esquerda">
                    <span>
                        <IconButton size="small" disabled={indiceAtivo === 0} onClick={() => moverAba(-1)} aria-label="Mover aba para a esquerda">
                            <Icon fontSize="small" sx={{ color: textoSecundario }}>chevron_left</Icon>
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Mover para a direita">
                    <span>
                        <IconButton size="small" disabled={indiceAtivo === tabelas.length - 1} onClick={() => moverAba(1)} aria-label="Mover aba para a direita">
                            <Icon fontSize="small" sx={{ color: textoSecundario }}>chevron_right</Icon>
                        </IconButton>
                    </span>
                </Tooltip>
                {tabelas.length < MAX_ABAS && (
                    <MDButton size="small" variant="text" color="info" onClick={duplicarAba} sx={{ color: darkMode ? "#90CAF9 !important" : undefined }}>
                        <Icon sx={{ mr: 0.5 }}>content_copy</Icon> Duplicar aba
                    </MDButton>
                )}
                {tabelas.length > 1 && (
                    <MDButton size="small" variant="text" color="error" onClick={excluirAba} sx={{ color: darkMode ? "#ff8a80 !important" : undefined }}>
                        <Icon sx={{ mr: 0.5 }}>delete_outline</Icon> Excluir aba
                    </MDButton>
                )}
                <MDBox flexGrow={1} />
                {sugestao && temConteudo(sugestao) && (
                    <MDButton size="small" variant="text" color="info" onClick={() => mudar(sugestao)} sx={{ color: darkMode ? "#90CAF9 !important" : undefined }}>
                        <Icon sx={{ mr: 0.5 }}>auto_fix_high</Icon> Sugestão
                    </MDButton>
                )}
                {temConteudo(config) && (
                    <MDButton
                        size="small"
                        variant="text"
                        color={darkMode ? "white" : "secondary"}
                        onClick={() => {
                            salvar({ ...configVazia(), nome: config.nome });
                            if (!tabelas.some((t, i) => i !== indiceAtivo && temConteudo(t))) onIncluir(false);
                        }}
                    >
                        <Icon sx={{ mr: 0.5 }}>clear_all</Icon> Limpar aba
                    </MDButton>
                )}
            </MDBox>

            <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                    <InputLabel sx={labelStyle}>Linhas</InputLabel>
                    <MDSelect isMulti name="linhas" placeholder="Ex.: Status, Ocorrência" value={config.linhas.map(opcao)} options={opcoesArea("linhas")} onChange={(o) => mudarArea("linhas", o)} />
                    <MDTypography variant="caption" sx={{ color: textoSecundario }}>
                        Até {LIMITES.linhas} campos, na ordem escolhida; {subtotais === "todos" ? "cada grupo ganha subtotal" : "o 1º campo ganha subtotal"}.
                    </MDTypography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <InputLabel sx={labelStyle}>Colunas</InputLabel>
                    <MDSelect isMulti name="colunas" placeholder="Opcional. Ex.: Situação" value={config.colunas.map(opcao)} options={opcoesArea("colunas")} onChange={(o) => mudarArea("colunas", o)} />
                    <MDTypography variant="caption" sx={{ color: textoSecundario }}>Abre os valores lado a lado (até {LIMITES.colunas}).</MDTypography>
                </Grid>
                <Grid item xs={12} md={4}>
                    <InputLabel sx={labelStyle}>Filtros no Excel</InputLabel>
                    <MDSelect isMulti name="filtros" placeholder="Opcional. Ex.: Negociador, Cliente" value={config.filtros.map(opcao)} options={opcoesArea("filtros")} onChange={(o) => mudarArea("filtros", o)} />
                    <MDTypography variant="caption" sx={{ color: textoSecundario }}>Até {LIMITES.filtros}; viram listas no topo da tabela no Excel (não mudam a prévia).</MDTypography>
                </Grid>

                <Grid item xs={12} lg={9}>
                    <InputLabel sx={labelStyle}>Valores</InputLabel>
                    {config.valores.map((v, i) => {
                        const numerico = tipos[v.campo] === "numero";
                        const como = MOSTRAR_COMO.find(m => m.id === (v.mostrarComo || "normal")) || MOSTRAR_COMO[0];
                        return (
                            <MDBox key={`${v.campo}-${i}`} display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={1}>
                                <MDBox sx={{ width: 140, flexShrink: 0 }}>
                                    <MDSelect
                                        name={`funcao-${i}`}
                                        value={FUNCOES_VALOR.filter(f => f.id === v.funcao).map(f => ({ value: f.id, label: f.label }))[0]}
                                        options={FUNCOES_VALOR.filter(f => numerico || !f.numerica).map(f => ({ value: f.id, label: f.label }))}
                                        onChange={(o) => mudarValor(i, { funcao: o?.value || "count" })}
                                    />
                                </MDBox>
                                <MDTypography variant="button" sx={{ color: textoSecundario }}>de</MDTypography>
                                <MDBox sx={{ flex: "1 1 180px", minWidth: 160 }}>
                                    <MDSelect
                                        name={`campo-${i}`}
                                        value={opcao(v.campo)}
                                        options={campos.map(opcao)}
                                        onChange={(o) => {
                                            const campo = o?.value || v.campo;
                                            const funcao = tipos[campo] === "numero" ? v.funcao : "count";
                                            mudarValor(i, { campo, funcao });
                                        }}
                                    />
                                </MDBox>
                                <MDBox sx={{ width: 190, flexShrink: 0 }}>
                                    <MDSelect
                                        name={`mostrar-${i}`}
                                        value={{ value: como.id, label: como.label }}
                                        options={MOSTRAR_COMO.map(m => ({ value: m.id, label: m.label }))}
                                        onChange={(o) => mudarValor(i, { mostrarComo: o?.value || "normal" })}
                                    />
                                </MDBox>
                                <MDBox sx={{ flex: "1 1 180px", minWidth: 160 }}>
                                    <MDInput
                                        fullWidth
                                        size="small"
                                        value={v.nome || ""}
                                        placeholder={nomeDoValor({ ...v, nome: "" }, rotulo)}
                                        onChange={(e) => mudarValor(i, { nome: e.target.value.slice(0, 60) })}
                                        inputProps={{ "aria-label": `Nome do valor ${i + 1}` }}
                                    />
                                </MDBox>
                                {!ehPercentual(v) && config.valores.length < LIMITES.valores && (
                                    <Tooltip title="Adicionar o mesmo valor em % do total">
                                        <IconButton size="small" onClick={() => duplicarComoPercentual(i)}>
                                            <Icon fontSize="small" sx={{ color: darkMode ? "#90CAF9" : "#1A73E8" }}>percent</Icon>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Tirar este valor">
                                    <IconButton size="small" onClick={() => mudar({ ...config, valores: config.valores.filter((_, j) => j !== i) })}>
                                        <Icon fontSize="small" sx={{ color: darkMode ? "#ff8a80" : "#C62828" }}>close</Icon>
                                    </IconButton>
                                </Tooltip>
                            </MDBox>
                        );
                    })}
                    <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                        {config.valores.length < LIMITES.valores && (
                            <MDButton size="small" variant="outlined" color="info" onClick={adicionarValor} sx={{ color: darkMode ? "#90CAF9 !important" : undefined, borderColor: darkMode ? "#90CAF9 !important" : undefined }}>
                                <Icon sx={{ mr: 0.5 }}>add</Icon> Adicionar valor
                            </MDButton>
                        )}
                        <MDTypography variant="caption" sx={{ color: textoSecundario }}>
                            {config.valores.length === 0
                                ? "Sem valor, a tabela conta os registros de cada grupo."
                                : "O mesmo campo pode entrar mais de uma vez: em valor e em % (botão %). O nome é opcional."}
                        </MDTypography>
                    </MDBox>
                </Grid>
                <Grid item xs={12} lg={3}>
                    <InputLabel sx={labelStyle}>Layout</InputLabel>
                    <MDBox display="flex" gap={0.75}>
                        {LAYOUTS.map(l => chip(layout === l.id, l.label, () => mudar({ ...config, layout: l.id })))}
                    </MDBox>
                    <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: textoSecundario }}>
                        {LAYOUTS.find(l => l.id === layout).ajuda}
                    </MDTypography>
                    {/* Com 1 ou 2 campos nas linhas só o 1º pode ter subtotal: a escolha só aparece a partir de 3 */}
                    {config.linhas.length > 2 && (
                        <MDBox mt={1.5}>
                            <InputLabel sx={labelStyle}>Subtotal</InputLabel>
                            <MDBox display="flex" gap={0.75}>
                                {SUBTOTAIS.map(s => chip(subtotais === s.id, s.label, () => mudar({ ...config, subtotais: s.id })))}
                            </MDBox>
                            <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: textoSecundario }}>
                                {subtotais === "todos"
                                    ? SUBTOTAIS.find(s => s.id === "todos").ajuda
                                    : `Um total por ${rotulo(config.linhas[0])}; os outros campos ficam sem subtotal.`}
                            </MDTypography>
                        </MDBox>
                    )}
                    {temData && (
                        <MDBox mt={1.5}>
                            <InputLabel sx={labelStyle}>Agrupar datas por</InputLabel>
                            <MDBox display="flex" gap={0.75}>
                                {AGRUPAR_DATAS.map(a => chip(config.agruparDatas === a.id, a.label, () => mudar({ ...config, agruparDatas: a.id })))}
                            </MDBox>
                            <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: textoSecundario }}>
                                Na planilha, entra uma coluna com a data agrupada.
                            </MDTypography>
                        </MDBox>
                    )}
                </Grid>
            </Grid>

            {previa && (
                <MDBox mt={2}>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={0.75}>
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: textoPrincipal }}>Prévia</MDTypography>
                        <MDTypography variant="caption" sx={{ color: textoSecundario }}>
                            {previa.totalLinhas.toLocaleString("pt-BR")} {previa.totalLinhas === 1 ? "grupo" : "grupos"}
                            {previa.cortadaLinhas ? ` · mostrando as primeiras ${previa.linhas.length} linhas` : ""}
                            {previa.cortadaColunas ? ` · mostrando ${previa.colunas.length} colunas` : ""}
                            {(previa.cortadaLinhas || previa.cortadaColunas) ? " (a planilha leva tudo)" : ""}
                        </MDTypography>
                    </MDBox>
                    <MDBox sx={{ overflow: "auto", maxHeight: 460, border: `1px solid ${borda}`, borderRadius: "8px" }}>
                        <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}>
                            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                {previa.temColunas && (
                                    <tr>
                                        {camposLinha.map(c => <th key={c || "todos"} style={celulaCab} />)}
                                        {previa.colunas.map(col => (
                                            <th key={col.chave} colSpan={nValores} style={{ ...celulaCab, textAlign: "center", borderLeft: `1px solid ${borda}` }}>
                                                {col.rotulos.join(" · ")}
                                            </th>
                                        ))}
                                        <th colSpan={nValores} style={{ ...celulaCab, textAlign: "center", borderLeft: `1px solid ${borda}` }}>Total</th>
                                    </tr>
                                )}
                                <tr>
                                    {camposLinha.map(c => (
                                        <th key={c || "todos"} style={celulaCab}>{c ? rotulo(c) : (config.linhas.length ? "Rótulos de linha" : "")}</th>
                                    ))}
                                    {chavesValor.map(chave => previa.valores.map((v, i) => (
                                        <th key={`${chave}-${i}`} style={{ ...celulaCab, textAlign: "right", borderLeft: bordaGrupo(i) }}>
                                            {nomeDoValor(v, rotulo)}
                                        </th>
                                    )))}
                                </tr>
                            </thead>
                            <tbody>
                                {previa.linhas.map((l, li) => {
                                    const subtotal = l.tipo === "subtotal";
                                    const num = subtotal ? celulaTotal : celulaNum;
                                    return (
                                        <tr key={`${l.tipo}-${l.rotulos.join("|")}-${li}`}>
                                            {rotulosDaLinha(l)}
                                            {previa.temColunas && previa.colunas.map(col => l.celulas[col.chave].map((valor, i) => (
                                                <td key={`${col.chave}-${i}`} style={{ ...num, borderLeft: bordaGrupo(i) }}>{formatarValor(valor, previa.valores[i])}</td>
                                            )))}
                                            {l.total.map((valor, i) => (
                                                <td key={`t-${i}`} style={{ ...(previa.temColunas || subtotal ? celulaTotal : celulaNum), borderLeft: bordaGrupo(i) }}>
                                                    {formatarValor(valor, previa.valores[i])}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td colSpan={camposLinha.length} style={{ ...celula, ...celulaTotal, textAlign: "left" }}>Total geral</td>
                                    {previa.temColunas && previa.colunas.map(col => previa.totalPorColuna[col.chave].map((valor, i) => (
                                        <td key={`g-${col.chave}-${i}`} style={{ ...celulaTotal, borderLeft: bordaGrupo(i) }}>{formatarValor(valor, previa.valores[i])}</td>
                                    )))}
                                    {previa.totalGeral.map((valor, i) => (
                                        <td key={`gt-${i}`} style={{ ...celulaTotal, borderLeft: bordaGrupo(i) }}>{formatarValor(valor, previa.valores[i])}</td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </MDBox>
                </MDBox>
            )}
        </Card>
    );
}

TabelaDinamicaPainel.propTypes = {
    dados: PropTypes.array.isRequired,
    tabelas: PropTypes.array.isRequired,
    ativa: PropTypes.number.isRequired,
    onTabelas: PropTypes.func.isRequired,
    onAtiva: PropTypes.func.isRequired,
    incluir: PropTypes.bool.isRequired,
    onIncluir: PropTypes.func.isRequired,
    sugestao: PropTypes.object,
    rotulo: PropTypes.func.isRequired,
};
